import { memo, useEffect, useId, useMemo, useRef } from 'react';
import FieldControlContext from './fieldControlContext';

/**
 * FormField - Standardized label + control wrapper with optional tooltip
 *
 * Replaces ad-hoc param-label / param-label-flex usage across modules.
 *
 * FormField owns field identity AND field shape. It mints the control id and
 * publishes it so a labelable child can adopt it, which is what associates the
 * caption with the control. Shape is declared, never inferred from whatever the
 * children happen to be this render:
 *
 *   as="field"    (default) one labelable control. Caption is <label for>.
 *   as="group"    two or more controls, or a control plus other interactive
 *                 content. The caption names the SECTION, so it emits no `for`;
 *                 the wrapper gets role="group" + aria-labelledby instead, and
 *                 each control keeps its own accessible name.
 *   as="caption"  no labelable control at all (static text, an image preview, a
 *                 drop target). The caption names nothing and emits no `for`.
 *
 * A childless FormField is a caption regardless of `as` — the label-with-no-child
 * form used where the control it captions is a sibling outside this component.
 *
 * Group and caption shapes render exactly the markup this component rendered
 * before ids existed, so they are visually and behaviorally unchanged. The
 * caption stays a <label> element in every shape: a bare `label` element selector
 * carries `user-select: none` (theme.css:36-47), which a <span> would silently
 * lose in panels and popovers.
 *
 * Why not clone an id onto the child: it no-ops on any component that ignores
 * `id`, producing a caption that points at nothing — broken, but passing an audit.
 *
 * @param {string|React.ReactNode} label - Label text or node
 * @param {React.ReactNode} [tooltip] - Optional tooltip element (e.g. <Tooltip><InfoIcon /></Tooltip>)
 * @param {'field'|'group'|'caption'} [as='field'] - Declared field shape
 * @param {string} [htmlFor] - Use this id instead of a minted one, for call sites
 *   that give the control an explicit id of their own. States the shared id once.
 * @param {React.ReactNode|function} children - The form control, or a function
 *   receiving `{ controlId }` for native <input>/<textarea> children, which
 *   cannot read context.
 */
const LABELABLE_SELECTOR = 'button, input, meter, output, progress, select, textarea';
// Tests fail on anything reported here (src/__tests__/setup.js). A sink rather than
// a console.error message match: the message wording is not a contract, and a
// console spy installed by any other test must not be able to silence this.
export const FORM_FIELD_DEV_REPORT = Symbol.for('__GLOWGHOST_FORM_FIELD_DEV_REPORT__');

function reportFieldDefect(message) {
  const sink = globalThis[FORM_FIELD_DEV_REPORT];
  if (Array.isArray(sink)) sink.push(message);
  console.error(message);
}
const CAPTION_SELECTOR = '.param-label, .param-label-flex';

const FormField = ({ label, tooltip, children, as = 'field', htmlFor }) => {
  const hasTooltip = !!tooltip;
  const generatedId = useId();
  const captionId = useId();
  const wrapperRef = useRef(null);

  const isFunctionChildren = typeof children === 'function';
  // null/false children are the childless case: several call sites render a
  // caption whose control is a sibling outside this component.
  const hasChildren = isFunctionChildren || (children != null && children !== false);
  const shape = hasChildren ? as : 'caption';
  const isField = shape === 'field';
  const isGroup = shape === 'group';

  const controlId = htmlFor || generatedId;
  // Only a field WITH A CAPTION publishes an id. Inside a group or a caption
  // there is no single control to name; and with no label there is nothing to
  // associate, so publishing would tell the primitive it is named when it is
  // not — which would suppress its aria-label and leave it nameless. Node rows
  // with `hideLabel` are exactly that case.
  const publishesId = isField && Boolean(label);
  // labelText rides along so a primitive can tell an aria-label that merely
  // repeats the caption (drop it, it would mask the association) from one that
  // says more than the caption does (keep it).
  const labelText = typeof label === 'string' ? label : null;
  const contextValue = useMemo(
    () => (publishesId ? { controlId, labelText } : null),
    [publishesId, controlId, labelText],
  );

  // Dev-only: the loudness that replaces R1's runtime registry. A field whose
  // label names nothing is the exact "audits as fixed while broken" failure this
  // component exists to prevent, so say it at the call site rather than shipping
  // a dangling `for`.
  useEffect(() => {
    if (import.meta.env.PROD) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const captionForMessage = labelText || '(node)';

    if (isField && label) {
      // getElementById, not querySelector: useId values contain ':', which is not
      // a valid CSS identifier.
      const control = document.getElementById(controlId);
      if (!control || !wrapper.contains(control)) {
        reportFieldDefect(
          `FormField "${captionForMessage}": the label points at #${controlId}, which does not exist inside the field, `
          + 'so nothing is associated. Fix with as="group" (several controls), as="caption" (no control), the '
          + 'function-children form for a native input, or htmlFor when the control has its own id.',
        );
      } else if (!control.matches(LABELABLE_SELECTOR)) {
        reportFieldDefect(
          `FormField "${captionForMessage}": the label points at a <${control.tagName.toLowerCase()}>, which cannot be `
          + 'labelled. Only button, input, meter, output, progress, select and textarea can. Use as="group" or as="caption".',
        );
      }
    }

    // Only a FIELD can have a duplicate caption. A group is DEFINED as holding
    // several controls, and those controls labelling themselves is the whole
    // point of the shape — counting them here made this fire on every correctly
    // annotated group.
    if (isField && wrapper.querySelectorAll(CAPTION_SELECTOR).length > 1) {
      reportFieldDefect(
        `FormField "${captionForMessage}": two captions for one field. The child primitive is rendering its own label prop `
        + 'as well. Drop one — FormField owns the caption wherever it is present.',
      );
    }
  });

  return (
    <FieldControlContext.Provider value={contextValue}>
      <div
        ref={wrapperRef}
        role={isGroup ? 'group' : undefined}
        aria-labelledby={isGroup && label ? captionId : undefined}
      >
        {label && (
          hasTooltip ? (
            <label
              className="param-label-flex"
              id={isGroup ? captionId : undefined}
              htmlFor={isField ? controlId : undefined}
            >
              {label}
              {tooltip}
            </label>
          ) : (
            <label
              className="param-label"
              id={isGroup ? captionId : undefined}
              htmlFor={isField ? controlId : undefined}
            >
              {label}
            </label>
          )
        )}
        {isFunctionChildren ? children({ controlId }) : children}
      </div>
    </FieldControlContext.Provider>
  );
};

export default memo(FormField);
